/**
 * Sync store — unified job tracker (May 25, 2026).
 *
 * Mirrors V1's ``SyncContext`` (April 2026 SOTA refactor) but rebuilt as
 * a Zustand store for V2's "no React Context spaghetti" rule. Single
 * source of truth for every long-running ingestion job in the app:
 *
 *   ┌─ Drive / OneDrive / SharePoint / Notion (docs)
 *   ├─ Gmail / Outlook (email)
 *   ├─ Teams / Slack / Google Chat (chat)
 *   ├─ Filesystem connector
 *   └─ Future SaaS connectors
 *
 * Job lifecycle:
 *   1. Caller fires the sync trigger (POST sync-with-filters / start-sync
 *      / force-sync) → BE returns a ``job_id``.
 *   2. Caller invokes ``trackJob(jobId, source, identifier)`` here.
 *   3. We open an ``EventSource`` on ``/api/v1/jobs/{job_id}/stream``
 *      and apply ``snapshot`` / ``progress`` / ``heartbeat`` events to
 *      the ``SyncJob`` slot in the store.
 *   4. On stream error / close we fall back to a 5s ``GET
 *      /api/v1/jobs/{job_id}/progress`` poll loop until terminal state.
 *   5. On terminal state (``completed`` / ``failed`` / ``cancelled``)
 *      the polling stops; the row stays in the store until ``dismissJob``
 *      is called (so the UI can render the final state).
 *
 * Components read via ``useSyncStore`` selectors (kept narrow to avoid
 * unnecessary re-renders) or via the per-domain adapters
 * (``useEmailSyncFor(emailAddress, provider)``, etc.).
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";

import { apiClient } from "@ando/core";

// ──────────────────────────────────────────────────────────────────────
// Domain types
// ──────────────────────────────────────────────────────────────────────

/**
 * Canonical job source. ``email`` / ``chat`` / ``docs`` are the three
 * V1 categories; ``filesystem`` is the LAN/NAS connector; ``other`` is
 * the catch-all for future vendors that don't fit the categories above.
 */
export type SyncSource = "email" | "chat" | "docs" | "filesystem" | "other";

/** Status the BE reports via the unified ``/jobs/{id}/stream`` schema. */
export type JobStatus =
  | "pending"
  | "processing"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "cancelling";

/**
 * Identifier rolled up from the per-source canonical fields:
 *   - email:   ``{provider, email_address}``
 *   - chat:    ``{provider, connection_key?}``
 *   - docs:    ``{provider, connection_key?}``
 *   - filesystem: ``{connector_id}``
 *
 * Stored together so the UI can derive a unique key per active sync
 * without having to know which source it's looking at.
 */
export interface SyncIdentifier {
  readonly provider?: string;
  readonly email_address?: string;
  readonly connection_key?: string;
  readonly connector_id?: string;
  readonly user_id?: string;
}

/** Live row stored under ``state.jobs[jobId]``. */
export interface SyncJob {
  readonly jobId: string;
  readonly source: SyncSource;
  readonly identifier: SyncIdentifier;
  readonly status: JobStatus;
  /** Optional sub-status string the BE uses for finer-grained UI hints. */
  readonly phase?: string;
  /** Progress 0-100. Always present once the BE emits its first event. */
  readonly progress: number;
  readonly totalItems: number;
  readonly completedItems: number;
  readonly failedItems: number;
  readonly skippedItems: number;
  /** Filename / subject the BE is currently processing (UI hint only). */
  readonly currentItem?: string;
  /** Terminal error message — set only when ``status === "failed"``. */
  readonly error?: string;
  /** Free-form source-specific blob (messages_processed, attachments_failed, …). */
  readonly extra: Readonly<Record<string, unknown>>;
  /** ``true`` while SSE is healthy; flips to ``false`` when polling kicks in. */
  readonly live: boolean;
  /** Monotonically increasing — used by selectors to short-circuit memoisation. */
  readonly version: number;
}

// ──────────────────────────────────────────────────────────────────────
// SSE / polling client (singleton, lives outside the store)
// ──────────────────────────────────────────────────────────────────────

interface ActiveStream {
  readonly source: EventSource | null;
  readonly pollTimer: number | null;
}

/**
 * One ``ActiveStream`` per ``jobId``. Kept outside the Zustand store
 * because EventSource is a stateful, non-serialisable resource — having
 * it in the state graph would break devtools and SSR.
 */
const activeStreams = new Map<string, ActiveStream>();
let nextStreamGeneration = 0;
const streamGenerations = new Map<string, number>();

const TERMINAL: ReadonlySet<JobStatus> = new Set(["completed", "failed", "cancelled"]);

// ``active`` here means "the BE is actively working OR queued" — i.e.
// the user can still see progress / cancel. ``cancelling`` is its own
// state because the UI needs to read the cancel-in-flight signal but
// the button MUST disappear (no double-cancel). Exposed as a shared
// predicate so ``SyncStatusPill``, ``CancelSyncButton``,
// ``UniversalSyncToast`` and ``useSyncBatch`` agree on what "active"
// means (V2 audit soft #7 — pre-fix the predicate was duplicated x4).
const ACTIVE: ReadonlySet<JobStatus> = new Set(["pending", "processing", "running"]);

/**
 * Shared "is this job still gating the user from starting a new one?"
 * predicate. Includes ``cancelling`` because Temporal cancellation is
 * asynchronous — the slot stays locked until the workflow actually
 * releases (June 10, 2026 audit ciclo 3, CRIT-WD1: the cancel-poll
 * loop in ``DocSyncManager`` was rolling its own wrong Set
 * (``queued``, ``in_progress``) that didn't even match this wire
 * enum).
 *
 * Exported so ``DocSyncManager``'s post-cancel poll, the unified
 * "Sincronizzazione in corso" pill, and any future surface that
 * needs to mirror this BE state machine all read from the SAME
 * source of truth.
 */
export const STILL_BUSY_STATUSES: ReadonlySet<JobStatus> = new Set([
  "pending",
  "processing",
  "running",
  "cancelling",
]);

export function isJobStillBusy(status: string | null | undefined): boolean {
  return Boolean(status) && STILL_BUSY_STATUSES.has(status as JobStatus);
}

const POLL_INTERVAL_MS = 5_000;

function isTerminal(status: JobStatus): boolean {
  return TERMINAL.has(status);
}

/**
 * ``true`` while the job is BE-side in progress (pending/processing/
 * running). Does NOT include ``cancelling`` — surfaces that need that
 * tone should branch explicitly.
 */
export function isJobActive(status: JobStatus): boolean {
  return ACTIVE.has(status);
}

function jobsStreamUrl(jobId: string): string {
  return `${apiClient.getBaseUrl()}/jobs/${encodeURIComponent(jobId)}/stream`;
}

function jobsProgressUrl(jobId: string): string {
  return `${apiClient.getBaseUrl()}/jobs/${encodeURIComponent(jobId)}/progress`;
}

// ──────────────────────────────────────────────────────────────────────
// Store
// ──────────────────────────────────────────────────────────────────────

interface SyncStoreState {
  /** Live + recently-completed jobs keyed by ``jobId``. */
  readonly jobs: Readonly<Record<string, SyncJob>>;
  /** ``true`` while a cancel request for ``jobId`` is in flight. */
  readonly cancelling: ReadonlySet<string>;
}

interface SyncStoreActions {
  /**
   * Start tracking a job the caller just kicked off. Opens SSE + applies
   * incremental updates. Idempotent — calling twice with the same jobId
   * is a no-op (the existing stream stays open).
   */
  trackJob(jobId: string, source: SyncSource, identifier: SyncIdentifier): void;
  /** Fire-and-forget cancel. Marks the job ``cancelling`` until the BE flips it. */
  cancelJob(jobId: string): Promise<void>;
  /** Remove a (typically terminal) job from the UI without affecting the BE. */
  dismissJob(jobId: string): void;
  /** Replace the snapshot for a job — called by the SSE handler. */
  applyJobStatus(jobId: string, patch: Partial<SyncJob>): void;
}

type SyncStore = SyncStoreState & SyncStoreActions;

const initialState: SyncStoreState = {
  jobs: {},
  cancelling: new Set<string>(),
};

export const useSyncStore = create<SyncStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      trackJob(jobId, source, identifier) {
        if (!jobId) return;
        if (activeStreams.has(jobId)) return; // idempotent

        // Seed a row so the UI can render the spinner immediately, even
        // before the BE pushes its first ``snapshot`` event.
        set(
          (s) => ({
            jobs: {
              ...s.jobs,
              [jobId]:
                s.jobs[jobId] ??
                ({
                  jobId,
                  source,
                  identifier,
                  status: "pending" as JobStatus,
                  progress: 0,
                  totalItems: 0,
                  completedItems: 0,
                  failedItems: 0,
                  skippedItems: 0,
                  extra: {},
                  live: true,
                  version: 0,
                } satisfies SyncJob),
            },
          }),
          false,
          `sync/trackJob[${jobId}]`,
        );

        attachStream(jobId);
      },

      async cancelJob(jobId) {
        if (!jobId) return;
        set(
          (s) => ({ cancelling: new Set([...s.cancelling, jobId]) }),
          false,
          `sync/cancelJob.begin[${jobId}]`,
        );
        try {
          // The unified jobs surface owns cancellation — per-source
          // overrides (chat-management/cancel-sync, etc.) wire to the
          // same Temporal API under the hood.
          await apiClient.post<{ status?: string }>(
            `/jobs/${encodeURIComponent(jobId)}/cancel`,
            {},
          );
        } catch {
          // Swallow — the next SSE snapshot or poll will reveal the
          // true state. UI shows the cancelling spinner until then.
        } finally {
          set(
            (s) => {
              const next = new Set(s.cancelling);
              next.delete(jobId);
              return { cancelling: next };
            },
            false,
            `sync/cancelJob.end[${jobId}]`,
          );
        }
      },

      dismissJob(jobId) {
        detachStream(jobId);
        set(
          (s) => {
            if (!(jobId in s.jobs)) return s;
            const { [jobId]: _removed, ...rest } = s.jobs;
            return { jobs: rest };
          },
          false,
          `sync/dismissJob[${jobId}]`,
        );
      },

      applyJobStatus(jobId, patch) {
        set(
          (s) => {
            const prev = s.jobs[jobId];
            if (!prev) return s;
            const merged: SyncJob = {
              ...prev,
              ...patch,
              extra: patch.extra ? { ...prev.extra, ...patch.extra } : prev.extra,
              version: prev.version + 1,
            };
            return { jobs: { ...s.jobs, [jobId]: merged } };
          },
          false,
          `sync/applyJobStatus[${jobId}]`,
        );

        const updated = get().jobs[jobId];
        if (updated && isTerminal(updated.status)) {
          // Close the stream — no more updates expected.
          detachStream(jobId);
        }
      },
    }),
    { name: "ando.sync" },
  ),
);

// ──────────────────────────────────────────────────────────────────────
// SSE wiring
// ──────────────────────────────────────────────────────────────────────

function appendQueryParam(url: string, key: string, value: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

function attachStream(jobId: string): void {
  // Sep 15, 2026 — ``EventSource`` cannot set headers and the Tauri desktop
  // has no cookie jar, so ``/jobs/{id}/stream`` answered 401 on desktop and
  // the store silently fell back to polling. Carry the Bearer as ``?token=``
  // (same contract as the WebSocket client; the BE ``get_sse_user_id``
  // accepts it). On web ``getAuthToken()`` is ``null`` → cookie flow as before.
  const generation = ++nextStreamGeneration;
  streamGenerations.set(jobId, generation);
  // Reserve the slot before the asynchronous keychain read so trackJob stays
  // idempotent even when called twice in the same tick.
  activeStreams.set(jobId, { source: null, pollTimer: null });

  void (async () => {
    let token: string | null = null;
    try {
      token = await apiClient.getAuthToken();
    } catch {
      // Cookie auth may still work; otherwise EventSource falls back to polling.
    }
    // detach + retrack creates a new generation. Never let the stale token
    // continuation overwrite or leak the replacement stream.
    if (
      streamGenerations.get(jobId) !== generation ||
      !useSyncStore.getState().jobs[jobId]
    ) {
      return;
    }
    const base = jobsStreamUrl(jobId);
    attachStreamWithUrl(jobId, token ? appendQueryParam(base, "token", token) : base);
  })();
}

function attachStreamWithUrl(jobId: string, url: string): void {
  // Prefer SSE — falls back to polling on ``onerror``.
  let source: EventSource | null = null;
  try {
    source = new EventSource(url, { withCredentials: true });
  } catch {
    source = null;
  }

  if (source) {
    source.addEventListener("snapshot", (ev) =>
      handleEvent(jobId, (ev as MessageEvent).data, true),
    );
    source.addEventListener("progress", (ev) =>
      handleEvent(jobId, (ev as MessageEvent).data, true),
    );
    source.addEventListener("heartbeat", () => {
      // No-op — used only to keep the stream alive through proxies.
    });
    source.onmessage = (ev) => handleEvent(jobId, ev.data, true);
    source.onerror = () => {
      // EventSource auto-retries forever; the V1 lesson is that proxies
      // sometimes silently 504 the stream while keeping ``readyState ===
      // OPEN``, so we ALWAYS layer a polling fallback below. Closing the
      // stream here flips the row to ``live=false`` and triggers polling.
      const cur = useSyncStore.getState().jobs[jobId];
      if (!cur || isTerminal(cur.status)) {
        detachStream(jobId);
        return;
      }
      source?.close();
      activeStreams.set(jobId, {
        source: null,
        pollTimer: schedulePoll(jobId),
      });
      useSyncStore.getState().applyJobStatus(jobId, { live: false });
    };

    activeStreams.set(jobId, { source, pollTimer: null });
    return;
  }

  // No EventSource (very old browser) → polling only.
  activeStreams.set(jobId, { source: null, pollTimer: schedulePoll(jobId) });
  useSyncStore.getState().applyJobStatus(jobId, { live: false });
}

function detachStream(jobId: string): void {
  const stream = activeStreams.get(jobId);
  if (!stream) return;
  stream.source?.close();
  if (stream.pollTimer !== null) {
    window.clearTimeout(stream.pollTimer);
  }
  activeStreams.delete(jobId);
  streamGenerations.delete(jobId);
}

export function _resetStreamsForTesting(): void {
  for (const jobId of [...activeStreams.keys()]) detachStream(jobId);
  useSyncStore.setState(initialState);
}

function schedulePoll(jobId: string): number {
  return window.setTimeout(async () => {
    try {
      const res = await apiClient.get<unknown>(`/jobs/${encodeURIComponent(jobId)}/progress`);
      const patch = parseJobPatch(res);
      if (patch) useSyncStore.getState().applyJobStatus(jobId, patch);
    } catch {
      // Transient error — keep polling.
    }
    const updated = useSyncStore.getState().jobs[jobId];
    if (!updated || isTerminal(updated.status)) {
      detachStream(jobId);
      return;
    }
    activeStreams.set(jobId, {
      source: null,
      pollTimer: schedulePoll(jobId),
    });
  }, POLL_INTERVAL_MS);
}

function handleEvent(jobId: string, raw: unknown, live: boolean): void {
  let parsed: unknown;
  try {
    parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return;
  }
  const patch = parseJobPatch(parsed);
  if (patch) {
    useSyncStore.getState().applyJobStatus(jobId, { ...patch, live });
  }
}

/**
 * Coerce a wire payload into a ``Partial<SyncJob>``. Tolerant of multiple
 * V1 shapes the BE emits across drive / email / chat surfaces (some
 * report ``messages_processed`` instead of ``completedItems`` etc.).
 * Unknown fields are dumped into ``extra`` so the per-domain adapter can
 * render them without losing fidelity.
 */
/**
 * Internal — exported for unit tests only. Production callers use the
 * store actions (``applyJobStatus``) instead.
 */
export function _parseJobPatchForTesting(payload: unknown): Partial<SyncJob> | null {
  return parseJobPatch(payload);
}

function parseJobPatch(payload: unknown): Partial<SyncJob> | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  // Mutable accumulator — ``SyncJob`` declares every field ``readonly``
  // so ``Partial<SyncJob>`` inherits that and TS rejects in-place writes.
  // We cast to ``Partial<SyncJob>`` once at return, after the build-up.
  type MutablePatch = {
    -readonly [K in keyof SyncJob]?: SyncJob[K];
  };
  const out: MutablePatch = {};

  if (typeof p.status === "string") {
    // ``completed_with_errors`` is a TERMINAL "done, with N failed files" state.
    // It's not in ``JobStatus`` — left raw it lands in limbo (neither
    // ``isTerminal`` nor ``isJobStillBusy``), so the SSE stream never closes and
    // the toast/pill spin forever (a folder sync with ≥1 failed file otherwise
    // shows "in progress" for hours). Normalize to ``completed`` here — the
    // single wire-ingress point — so every predicate treats it as done. The
    // failure count survives separately in the item counts, so the toast still
    // surfaces "N errors".
    out.status = (p.status === "completed_with_errors" ? "completed" : p.status) as JobStatus;
  }
  if (typeof p.phase === "string") {
    out.phase = p.phase;
  }
  // Jun 26, 2026 — accept naming families:
  //   * ``total_items`` / ``completed_items`` — original V1 wire shape
  //   * ``total_files`` / ``files_completed`` — REST shape emitted by
  //     ``build_ingestion_status_response`` (sync_helpers.py)
  //
  // Jun 30, 2026 — added THIRD shape: short names ``total`` /
  // ``processed`` / ``successful`` / ``failed`` / ``skipped`` emitted
  // by the WebSocket broadcast (``_build_sync_event_payload`` in
  // ``src/temporal/activities/ingestion_activities.py:2389``). Senza
  // questi alias il payload WS finiva tutto in ``extras`` → toast
  // bloccato a "in progress…" anche con job avviato + total_files
  // popolato (cfr. incident drive Test-Brasile 30 giu 2026).
  // I tre wire shape coesistono perché ognuno serve un canale diverso
  // (REST vs WS vs legacy V1); il FE accetta tutti come union.
  const totalItems =
    (typeof p.total_items === "number" ? p.total_items : undefined) ??
    (typeof p.total_files === "number" ? p.total_files : undefined) ??
    (typeof p.total === "number" ? p.total : undefined);
  // Jun 30, 2026 (code-review): priority ``successful`` > ``processed``.
  // Razionale: nel WS payload BE (``_build_sync_event_payload``)
  // ``processed`` = items elaborati (successful + failed + skipped),
  // ``successful`` = solo riusciti. Il toast vuole "completed" =
  // "riusciti" (gli altri sono già contati in failed/skipped). Mappare
  // ``processed`` su ``completedItems`` darebbe overcounting (con 100
  // processed e 80 successful → toast mostra 100 ok poi 80 — flip).
  // Preferisci ``successful`` quando presente; fallback ``processed``
  // (V1 wire shape pre-2026 lo usava come "successful").
  const completedItems =
    (typeof p.completed_items === "number" ? p.completed_items : undefined) ??
    (typeof p.files_completed === "number" ? p.files_completed : undefined) ??
    (typeof p.successful === "number" ? p.successful : undefined) ??
    (typeof p.processed === "number" ? p.processed : undefined);
  const failedItems =
    (typeof p.failed_items === "number" ? p.failed_items : undefined) ??
    (typeof p.files_failed === "number" ? p.files_failed : undefined) ??
    (typeof p.failed === "number" ? p.failed : undefined);
  const skippedItems =
    (typeof p.skipped_items === "number" ? p.skipped_items : undefined) ??
    (typeof p.skipped_files === "number" ? p.skipped_files : undefined) ??
    (typeof p.skipped === "number" ? p.skipped : undefined);

  if (typeof p.progress === "number") {
    out.progress = Math.max(0, Math.min(100, p.progress));
  } else if (typeof p.progress_percentage === "number") {
    out.progress = Math.max(0, Math.min(100, p.progress_percentage));
  } else if (typeof p.percentage === "number") {
    // BE WebSocket wire shape (``_build_sync_event_payload``) — Jun 30, 2026
    out.progress = Math.max(0, Math.min(100, p.percentage));
  } else if (completedItems !== undefined && totalItems !== undefined && totalItems > 0) {
    out.progress = Math.round((completedItems / totalItems) * 100);
  }
  if (totalItems !== undefined) out.totalItems = totalItems;
  if (completedItems !== undefined) out.completedItems = completedItems;
  if (failedItems !== undefined) out.failedItems = failedItems;
  if (skippedItems !== undefined) out.skippedItems = skippedItems;
  if (typeof p.current_item === "string") out.currentItem = p.current_item;
  if (typeof p.current_file === "string") out.currentItem = p.current_file;
  if (typeof p.error === "string") out.error = p.error;

  // Anything else rides in ``extra`` so per-domain adapters can still
  // render source-specific counters (messages_processed,
  // attachments_failed, channels_seen, …).
  const known = new Set([
    "status",
    "phase",
    "progress",
    "progress_percentage",
    "percentage",
    "total_items",
    "total_files",
    "total",
    "completed_items",
    "files_completed",
    "processed",
    "successful",
    "successful_files",
    "failed_items",
    "files_failed",
    "failed",
    "failed_files",
    "skipped_items",
    "skipped_files",
    "skipped",
    "current_item",
    "current_file",
    "error",
  ]);
  const extras: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p)) {
    if (!known.has(k)) extras[k] = v;
  }
  if (Object.keys(extras).length > 0) out.extra = extras;

  return Object.keys(out).length > 0 ? out : null;
}

// ──────────────────────────────────────────────────────────────────────
// Selectors (keep narrow — wide selectors trigger re-renders)
// ──────────────────────────────────────────────────────────────────────

/** All currently-tracked jobs (terminal + active). Use sparingly. */
export const selectAllJobs = (s: SyncStoreState): readonly SyncJob[] => Object.values(s.jobs);

/** Convenience hook that yields the array WITHOUT triggering on every store change. */
export function useAllSyncJobs(): readonly SyncJob[] {
  return useSyncStore(useShallow((s) => Object.values(s.jobs)));
}

/** Jobs filtered by source. */
export function useSyncJobsForSource(source: SyncSource): readonly SyncJob[] {
  return useSyncStore(useShallow((s) => Object.values(s.jobs).filter((j) => j.source === source)));
}

/**
 * Most recent job matching the given source + identifier. Returns
 * ``null`` when nothing is being tracked. Used by per-capability UIs
 * that show a single progress widget per connection.
 */
export function useLatestJobFor(
  source: SyncSource,
  predicate: (id: SyncIdentifier) => boolean,
): SyncJob | null {
  return useSyncStore((s) => {
    const matching = Object.values(s.jobs).filter(
      (j) => j.source === source && predicate(j.identifier),
    );
    if (matching.length === 0) return null;
    // Map insertion order = creation order in Object.values. Return the
    // most-recently-added entry that is not terminal; fall back to the
    // newest terminal entry so the UI keeps showing the result post-sync.
    const live = matching.filter((j) => !isTerminal(j.status));
    return live.at(-1) ?? matching.at(-1) ?? null;
  });
}

/** Whether a job is currently being cancelled (cancel request in flight). */
export function useIsCancellingJob(jobId: string | null | undefined): boolean {
  return useSyncStore((s) => (jobId ? s.cancelling.has(jobId) : false));
}
